import * as THREE from 'three';
import { simToWorld } from '@somnhold/render';

/** Sim units to world units (a radius). */
const toWorld = (r: number): number => simToWorld(r, 0).x;

const ring = (inner: number, color: string, opacity: number): THREE.Mesh => {
  const m = new THREE.Mesh(
    new THREE.RingGeometry(inner, 1, 72).rotateX(-Math.PI / 2),
    new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthTest: false, depthWrite: false }),
  );
  m.renderOrder = 20;
  m.visible = false;
  return m;
};

/**
 * Aiming on PC, drawn on the ground: while a skill key is held (or a skill is being aimed), its reach around
 * the hero and where it will land (the point already clamped to that reach, as the simulation does), or the
 * nightmare or ally it will take; with nothing held, a ring under the nightmare the cursor is on.
 */
export class AimMarks {
  private reach = ring(0.985, '#eceef2', 0.5);
  private areaFill = ring(0, '#ff4f3a', 0.09);
  private areaEdge = ring(0.94, '#ff8a6e', 0.85);
  private target = ring(0.7, '#ff4f3a', 0.9);
  private all = [this.reach, this.areaFill, this.areaEdge, this.target];

  constructor(private scene: THREE.Scene) {
    for (const m of this.all) scene.add(m);
  }

  hide(): void {
    for (const m of this.all) m.visible = false;
  }

  /** The skill's reach around the hero (world position of its feet). */
  showReach(at: THREE.Vector3, range: number): void {
    this.reach.visible = true;
    this.reach.position.set(at.x, at.y + 0.05, at.z);
    this.reach.scale.setScalar(toWorld(range));
  }

  /** Where a ground skill lands: its area (sim point, at the height of `y`). */
  showArea(x: number, y: number, h: number, radius: number): void {
    const p = simToWorld(x, y, new THREE.Vector3(), h + 0.06);
    const r = toWorld(radius);
    for (const m of [this.areaFill, this.areaEdge]) {
      m.visible = true;
      m.position.copy(p);
      m.scale.setScalar(r);
    }
  }

  /** A ring under a nightmare (red) or an ally (green), at its feet. */
  showTarget(at: THREE.Vector3, ally = false): void {
    this.target.visible = true;
    this.target.position.set(at.x, at.y + 0.06, at.z);
    this.target.scale.setScalar(0.62);
    (this.target.material as THREE.MeshBasicMaterial).color.set(ally ? '#7fd36a' : '#ff4f3a');
  }

  dispose(): void {
    for (const m of this.all) {
      this.scene.remove(m);
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    }
  }
}
